import { useLoadAllPermissions } from '@Hooks/Permissions';
import { type ApiPermission } from '@Model';
import { type Awaitable, type Column, Table, useDebouncedCallback, useLogger } from '@synqo/frontend-component-library';
import { useCallback, useEffect, useMemo, useRef } from 'react';

interface PermissionsListProps {
    /** Permission keys currently linked to this group */
    linkedPermissions: string[];
    extraColumns?: Column<ApiPermission>[];
    updatePermissions?: (newKeys: string[]) => Awaitable<boolean>;
}

interface PermissionWithId extends ApiPermission {
    id: string;
}

export function PermissionsList({ updatePermissions, linkedPermissions, extraColumns }: PermissionsListProps) {
    const { data: loadedPermissions } = useLoadAllPermissions();
    const { error } = useLogger();

    const selectedKeys = linkedPermissions;

    const isUpdatingRef = useRef(false);
    const pendingKeysRef = useRef<Set<string> | null>(null);

    const permissions = useMemo<PermissionWithId[]>(
        () => loadedPermissions.map((p) => ({ ...p, id: p.key })),
        [loadedPermissions],
    );
    const unlicensedPermissionKeys = useMemo(
        () => new Set(permissions.filter((p) => !p.is_licensed).map((p) => p.id)),
        [permissions],
    );
    const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

    // @ai-generated 2026-06-19T20:56:25Z by matthias.gessinger@synqony.com using Opus 4.8
    const processChangesCallback = useCallback(async () => {
        const pendingKeys = pendingKeysRef.current;

        if (!updatePermissions || pendingKeys === null) {
            return;
        }

        pendingKeysRef.current = null;
        isUpdatingRef.current = true;

        try {
            // spreading pendingKeys turns a Set into an array
            await updatePermissions([...pendingKeys]);
        } catch (e) {
            error(`Failed to update permissions: ${JSON.stringify(e)}`);
        } finally {
            isUpdatingRef.current = false;
        }
    }, [updatePermissions, error]);

    const processChangesDebounced = useDebouncedCallback(processChangesCallback, 100);

    // Each debounced event carries a near-complete key list that differs from the unchanged
    // `selectedKeys` snapshot by exactly the row that triggered it. We fold every such diff into a
    // single pending set so a group toggle (one event per row) collapses into one update call.
    // @ai-generated 2026-06-19T20:56:25Z by matthias.gessinger@synqony.com using Opus 4.8
    const handleSelectionChange = useCallback(
        (value: string[] | ((prevValue: string[]) => string[])) => {
            if (!updatePermissions) {
                return;
            }

            const newKeys = typeof value === 'function' ? value([...selectedKeySet]) : value;
            const newKeySet = new Set(newKeys);
            const pendingKeys = pendingKeysRef.current ?? new Set(selectedKeySet);

            for (const key of selectedKeySet) {
                if (!newKeySet.has(key) && !unlicensedPermissionKeys.has(key)) {
                    pendingKeys.delete(key);
                }
            }

            for (const key of newKeys.filter((k) => !k.includes('category'))) {
                if (!selectedKeySet.has(key) && !unlicensedPermissionKeys.has(key)) {
                    pendingKeys.add(key);
                }
            }

            pendingKeysRef.current = pendingKeys;
            processChangesDebounced();
        },
        [selectedKeySet, unlicensedPermissionKeys, processChangesDebounced, updatePermissions],
    );

    useEffect(() => {
        if (!isUpdatingRef.current) {
            pendingKeysRef.current = null;
        }
    }, [linkedPermissions]);

    const permissionsColumns: Column<ApiPermission>[] = useMemo(
        () => [
            {
                name: 'category',
                label: 'Berechtigungsgruppe',
                enableGrouping: true,
            },
            {
                name: 'label',
                label: 'Bezeichnung',
            },
            ...(extraColumns ?? []),
        ],
        [extraColumns],
    );

    const isPermissionLocked = useCallback(
        (id: string) => unlicensedPermissionKeys.has(id),
        [unlicensedPermissionKeys],
    );

    return (
        <Table<PermissionWithId>
            items={permissions}
            columns={permissionsColumns}
            name="Permissions"
            objectSelector={{
                selectedObjectIds: selectedKeys,
                setSelectedObjectIds: handleSelectionChange,
                locked: isPermissionLocked,
            }}
            initialGrouping={['category']}
            disableTopToolbar
        />
    );
}
